'use client';

// for phone number
// it will show you the phone country code but will not send you the same.
// to get the country code - you need to get it from the `country.code`

import { useEffect, useState } from 'react';
import { useTranslations } from 'next-intl';
import styles from './AddressForm.module.css';
interface Country {
  id: number;
  name: string;
  iso2: string;
  country_code: string;
  phone_code: string;
}

interface State {
  id: number;
  name: string;
  iso2: string;
  state_code: string;
}

interface City {
  id: number;
  name: string;
  iso2: string;
}

export interface AddressObject {
  firstName?: string;
  lastName?: string;
  address1?: string;
  address2?: string;
  postalCode?: string;
  city?: string;
  state?: State;
  country?: Country;
  phone?: string;
}

export const AddressForm = ({
  className,
  onSubmit,
  onChange,
  filterCountries,
  submitBtnVisible,
}: {
  className?: string;
  submitBtnVisible?: boolean;
  onSubmit?: ({
    firstName,
    lastName,
    address1,
    address2,
    postalCode,
    city,
    state,
    country,
    phone,
  }: AddressObject) => any;
  onChange?: ({
    firstName,
    lastName,
    address1,
    address2,
    postalCode,
    city,
    state,
    country,
    phone,
  }: AddressObject) => any;
  filterCountries?: string[];
}) => {
  const t = useTranslations('common');
  const [countries, setCountries] = useState<Country[]>([]);
  const [states, setStates] = useState<State[]>([]);
  const [cities, setCities] = useState<City[]>([]);

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [phone, setPhone] = useState<string>();
  // const [email, setEmail] = useState<string>();
  const [address1, setAddress1] = useState('');
  const [address2, setAddress2] = useState('');
  const [city, setCity] = useState<string>();
  const [postalCode, setPostalCode] = useState<string>();
  const [state, setState] = useState<State>();
  const [country, setCountry] = useState<Country>();
  const [formLoading] = useState(false);

  const submitEnabled =
    lastName &&
    phone &&
    phone.length > 5 &&
    //   phone != 0 &&
    //   String(phone).length > 5 &&
    //   email &&

    // email.match(/^[\w-\.]+@([\w-]+\.)+[\w-]{2,4}$/g) &&
    address1 &&
    city &&
    state &&
    country &&
    !formLoading;

  useEffect(() => {
    fetchCountries();
  }, []);

  const fetchCountries = async () => {
    try {
      const response = await fetch(
        'https://public-assets.uh-static.com/country-state-city/countries.json',
      );
      let data = await response.json();
      if (filterCountries) {
        data = data.filter((country: Country) =>
          filterCountries.includes(country.iso2),
        );
      }
      console.log({ data });
      setCountries(data);
    } catch (error) {
      console.error('Error fetching countries:', error);
    }
  };

  const fetchStates = async (countryId: string) => {
    try {
      const response = await fetch(
        `https://public-assets.uh-static.com/country-state-city/states/${countryId}.json`,
      );
      const data = await response.json();
      setStates(data);
    } catch (error) {
      console.error('Error fetching states:', error);
    }
  };

  const fetchCities = async (stateId: string) => {
    try {
      const response = await fetch(
        `https://public-assets.uh-static.com/country-state-city/cities/${stateId}.json`,
      );
      const data = await response.json();
      setCities(data);
    } catch (error) {
      console.error('Error fetching cities:', error);
    }
  };

  const handleCountryChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedOption = event.target.value;

    const selectedCountry = countries.find(
      (country) => country.name === selectedOption,
    );
    if (selectedCountry) {
      setCountry(selectedCountry);
      setState(undefined);
      setCity(undefined);
    }
    // Fetch states based on the selected country
    fetchStates(selectedCountry?.iso2 as string);
  };

  const handleStateChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedOption = event.target.value;

    const selectedState = states.find((state) => state.name === selectedOption);
    setState(selectedState);
    setCity(undefined);

    // Fetch cities based on the selected state
    fetchCities(selectedState?.state_code as string);
  };

  const handleCityChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedCityId = event.target.value;
    setCity(selectedCityId);
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (onSubmit) {
      onSubmit({
        firstName,
        lastName,
        address1,
        address2,
        postalCode,
        city,
        state,
        country,
        phone,
      });
    }
    //   onSubmit({
    //     address1,
    //     address2,
    //     postalCode,
    //     city,
    //     state,
    //     country,
    //   });
  };

  useEffect(() => {
    if (!onChange) return;
    onChange({
      firstName,
      lastName,
      address1,
      address2,
      postalCode,
      city,
      state,
      country,
      phone,
    });
  }, [
    firstName,
    lastName,
    address1,
    address2,
    postalCode,
    city,
    state,
    country,
    phone,
  ]);

  return (
    <form className={[styles.root, className].filter(Boolean).join(' ')}>
      <div className="form">
        <div className="shipping-details block">
          <p>{t('addressForm.text.shippingDetails')}</p>
          <div className={styles.inputRows}>
            <div className={styles.row}>
              <input
                type="text"
                name="firstName"
                placeholder={t('addressForm.input.placeholder.firstName')}
                // required
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
              />
              <input
                type="text"
                name="lastName"
                placeholder={t('addressForm.input.placeholder.lastNameRequired')}
                required
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
              />
            </div>

            <div className={`${styles.row} ${styles.col3}`}>
              <div className="select-wrapper">
                <select
                  id="country"
                  value={country?.name || ''}
                  onChange={handleCountryChange}
                  required
                >
                  <option value="">{t('addressForm.content.country')}</option>
                  {countries.map((country) => (
                    <option key={country.iso2} value={country.name}>
                      {country.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className={`select-wrapper ${!country ? 'disabled' : ''}`}>
                <select
                  id="state"
                  value={state?.name || ''}
                  onChange={handleStateChange}
                  disabled={!country}
                  required
                >
                  <option value="">{t('addressForm.content.state')}</option>
                  {states.map((state) => (
                    <option key={state.id} value={state.name}>
                      {state.name}
                    </option>
                  ))}
                </select>
              </div>
              <div
                className={`select-wrapper ${
                  !(country && state) ? 'disabled' : ''
                }`}
              >
                <select
                  id="city"
                  value={city || ''}
                  onChange={handleCityChange}
                  required
                  disabled={!(country && state)}
                >
                  <option value="">{t('addressForm.content.city')}</option>
                  {cities.map((city) => (
                    <option key={city.id} value={city.iso2}>
                      {city.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className={`${styles.row} ${styles.noGap}`}>
              {
                <span className={styles.phoneCode}>
                  {country?.phone_code ? '+' : null} {country?.phone_code}
                </span>
              }
              <input
                type="tel"
                name="phone"
                placeholder={t('addressForm.input.placeholder.phoneNumber')}
                className={styles.leftNoBorder}
                required
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
              />
            </div>

            <div className={styles.row}>
              <input
                type="text"
                name="postalCode"
                placeholder={t('addressForm.input.placeholder.postalCode')}
                required
                value={postalCode}
                onChange={(e) => setPostalCode(e.target.value)}
              />
              <input
                type="text"
                name="address1"
                placeholder={t('addressForm.input.placeholder.addressLine')}
                required
                value={address1}
                onChange={(e) => setAddress1(e.target.value)}
              />
            </div>

            <input
              type="text"
              name="address2"
              placeholder={t('addressForm.input.placeholder.addressLine2')}
              required
              value={address2}
              onChange={(e) => setAddress2(e.target.value)}
            />

            <div className={styles.row}></div>
          </div>
        </div>
        {submitBtnVisible && onSubmit && (
          <button
            className="submit"
            type="submit"
            onClick={handleSubmit}
            disabled={!submitEnabled}
          >
            {t('addressForm.button.save')}
          </button>
        )}
      </div>
    </form>
  );
};
